/**
 * SmsOptIn — case « Texto avec lien » dans un formulaire de création (document à signer · communication « Envois ») :
 * charge le texte proposé (lien = {lien}, remplacé à l'envoi) dès qu'un client est choisi, laisse l'admin le valider /
 * modifier, et remonte {send_sms, sms_text} au parent via onChange.
 * previewBody : {kind, client_id, title?, comm_type?} — rechargé quand le client ou le type change (pas à chaque frappe du titre).
 */
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/api";
import SmsComposer from "./SmsComposer";

export default function SmsOptIn({ previewBody, checked, onChange, label = "Texto avec lien (le client reçoit seulement le lien, jamais la pièce)", testId = "sms-optin", accent = "accent-blue-900" }) {
  const [p, setP] = useState(null);
  const [text, setText] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const edited = useRef(false);
  const toEdited = useRef(false);
  const lastClient = useRef(previewBody?.client_id);
  const key = `${previewBody?.kind}|${previewBody?.client_id}|${previewBody?.comm_type || ""}|${previewBody?.title || ""}`;

  useEffect(() => {
    if (!checked || !previewBody?.client_id) return;
    if (lastClient.current !== previewBody.client_id) { lastClient.current = previewBody.client_id; edited.current = false; toEdited.current = false; }
    let alive = true;
    setLoading(true); setErr("");
    const t = setTimeout(() => {
      api.post("/admin/sms-outreach/preview", previewBody)
        .then((r) => { if (!alive) return; setP(r.data); if (!toEdited.current) setTo(r.data.to || ""); if (!edited.current) setText(r.data.text || ""); })
        .catch((e) => alive && setErr(e?.response?.data?.detail || "Aperçu du texto indisponible"))
        .finally(() => alive && setLoading(false));
    }, p ? 500 : 0);
    return () => { alive = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, key]);

  useEffect(() => { onChange?.({ send_sms: checked, sms_text: checked ? text : "", sms_to: checked ? to : "" }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, text, to]);

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" checked={checked} onChange={(e) => onChange?.({ send_sms: e.target.checked, sms_text: e.target.checked ? text : "", sms_to: to })} className={`w-4 h-4 ${accent}`} data-testid={`${testId}-checkbox`} />
        {label}
      </label>
      {checked && !previewBody?.client_id && <p className="text-[11px] text-amber-700" data-testid={`${testId}-no-client`}>Choisissez d'abord le client pour préparer le texte du texto.</p>}
      {checked && previewBody?.client_id && (
        <div className="bg-white/70 border border-orange-200 rounded-xl p-3" data-testid={`${testId}-box`}>
          {loading && !p && <div className="text-slate-400 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div>}
          {err && <p className="text-xs text-rose-700">{err}</p>}
          {p && <SmsComposer to={to} onTo={(v) => { toEdited.current = true; setTo(v); }} text={text} onText={(v) => { edited.current = true; setText(v); }} links={{ link: "{lien}" }} phoneMissing={p.phone_missing} clientName={p.client_name} idPrefix={testId} />}
          {p && <p className="text-[11px] text-slate-500 mt-1">« {"{lien}"} » sera remplacé par le lien réel au moment de l'envoi.</p>}
        </div>
      )}
    </div>
  );
}
